// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';
import { withStyles } from "tss-react/mui";
import { Dialog, DialogContent, DialogActions } from '@mui/material';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import { filterXSS } from 'xss';

import container from '@core/di';
import { DeliverableDTO, DeliverableDownloadAccessDTO } from '@shared/models/deliverable';
import DeliverablesStore from '@shared/stores/deliverables';
import Flex from '@shared/components/Flex';
import Button from '@shared/components/Button';
import Loading from '@shared/components/Loading';
import Clipboard from '@shared/components/Clipboard';
import DialogTitle from '@shared/components/DialogTitle';
import { fields } from '@modules/Private/submodules/JobRequests/pages/JobRequestDetails/forms/download-instructions';

export interface DownloadInstructionsProps {
  className?: string;
  deliverableId: DeliverableDTO['id'];
  close: () => any;
}

interface Field {
  label: string;
  name: string;
  className?: string;
  render?: () => React.ReactNode;
}

@observer
class DownloadInstructions extends React.Component<DownloadInstructionsProps> {
  @observable data: DeliverableDownloadAccessDTO;
  @observable loading = false;
  private store = container.get<DeliverablesStore>(DeliverablesStore.diToken);

  constructor(props: DownloadInstructionsProps) {
    super(props);

    this.initialize();
    makeObservable(this);
  }

  private initialize = async () => {
    this.loading = true;

    try {
      this.data = await this.store.getDownloadInstruction(this.props.deliverableId);
      this.loading = false;
    }
    catch {
      this.loading = false;
    }
  }

  private get fields(): Array<Field> {
    const classes = withStyles.getClasses(this.props);

    return [
      {
        label: 'FTP server:',
        name: fields.link,
        render: () => (
          <a href={`//${this.data?.link}`} className={classes.ftpLink} target="_blank" rel="noopener noreferrer">
            {this.data?.link}
          </a>
        )
      },
      {
        label: 'Login:',
        name: fields.userName,
        render: () => (
          <Flex alignItems="center">
            <span className={classes.value}>{this.data?.userName}</span>
            <Clipboard
              text={this.data?.userName}
            />
          </Flex>
        )
      },
      {
        label: 'Password:',
        name: fields.password,
        render: () => (
          <Flex alignItems="center">
            <span className={classes.value}>{this.data?.password}</span>
            <Clipboard
              text={this.data?.password}
            />
          </Flex>
        )
      },
      {
        label: 'Download instructions:',
        name: fields.notes,
        render: () => {
          return (
            <span
              dangerouslySetInnerHTML={{
                __html: filterXSS(this.data?.notes.replace(/\n/ig, '<br />'))
              }}
            />
          );
        }
      }
    ];
  }

  render() {
    const { close } = this.props;
    const classes = withStyles.getClasses(this.props);

    if (this.loading) {
      return (
        <Dialog open classes={{ paper: cx(classes.root, classes.rootLoading) }}>
          <Loading />
        </Dialog>
      );
    }

    if (!this.loading && isEmpty(this.data)) {
      return (
        <Dialog open classes={{ paper: classes.root }}>
          <DialogContent>
            <h6 className={classes.subHeading}>
              There is no data
            </h6>
          </DialogContent>
          <DialogActions>
            <Button
              className={classes.actionBtn}
              text="Close"
              variant="contained"
              size="small"
              onClick={close}
            />
          </DialogActions>
        </Dialog>
      );
    }

    return (
      <Dialog
        open
        classes={{ paper: classes.root }}
      >
        <DialogTitle
          heading="Download instructions"
          onClose={close}
        />
        <DialogContent className={classes.content}>
          <h6 className={classes.subHeading}>
            Your deliverable is available for download.
          </h6>
          {this.fields.map(({ name = '', label, render }) => (
            <Flex key={label} alignItems="center" className={cx(classes[name], classes.field)}>
              <span className={classes.label}>{label}</span>
              <div className={classes.value}>
                {render ? render() : (this.data || {})[name]}
              </div>
            </Flex>
          ))}
        </DialogContent>
      </Dialog >
    );
  }
}

const DownloadInstructionsStyled = withStyles(
  DownloadInstructions, 
  (theme, props) => ({
    "root": {
      width: 610,
      maxWidth: 'none',
      margin: 0,
      border: 'none',
    },
    "rootLoading": {
      height: 300
    },
    "subHeading": {
      fontSize: 16,
      color: theme.palette.colors.black,
      marginBottom: theme.spacing(6),
    },
    "content": {
      padding: theme.spacing(5, 5, 8),
      fontSize: 15,
    },
    "actions": {
      justifyContent: 'flex-start',
      backgroundColor: theme.palette.colors.greyLight5,
      padding: theme.spacing(4),
    },
    "actionBtn": {
      fontSize: 16,
    },
    "label": {
      color: theme.palette.colors.greyDark,
      fontSize: 16,
    },
    "value": {
      fontSize: 16,
      paddingLeft: theme.spacing(2),
      color: theme.palette.colors.black,
      fontWeight: 500,
    },
    "ftpLink": {
      ...theme.shortcuts.borderPrimarySolid
    },
    "notes": {
      marginTop: theme.spacing(6),
      flexDirection: 'column',
      alignItems: 'flex-start',

      '& $value': {
        fontWeight: 400,
        paddingLeft: 0,
      }
    },
    "field": {
      marginBottom: theme.spacing(1),
    }
  })
);

export default DownloadInstructionsStyled;